import React, { useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';

// Shared visual language for MonitorDashboard, DeanDashboard, and
// DepartmentMonitorDashboard — extracted verbatim from MonitorDashboard.js so
// all three stay pixel-identical instead of drifting via copy-paste.

// ─── Constants ────────────────────────────────────────────────────────────────
export const MAROON = '#8e2c2c';
export const COLORS = ['#4A90E2','#27AE60','#E74C3C','#F39C12','#9B59B6','#1ABC9C','#E67E22','#3498DB','#e84393','#16a085'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const fmtShort = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—';

export function rateColor(r) {
  return r >= 75 ? '#27AE60' : r >= 45 ? '#F39C12' : '#E74C3C';
}

export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px', boxShadow:'0 4px 16px rgba(0,0,0,0.12)', fontSize:12 }}>
      <p style={{ margin:'0 0 4px', fontWeight:700, color:'#555', fontSize:11 }}>{label}</p>
      {payload.map((p,i) => (
        <p key={i} style={{ margin:'2px 0', color:p.color, fontWeight:600 }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick, hoverable }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hoverable && setHov(true)}
      onMouseLeave={() => hoverable && setHov(false)}
      style={{
        background:'#fff', borderRadius:14, padding:18,
        boxShadow: hov ? '0 8px 28px rgba(0,0,0,0.14)' : '0 2px 10px rgba(0,0,0,0.07)',
        transition:'box-shadow 0.2s, transform 0.15s',
        transform: hov ? 'translateY(-2px)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...style
      }}
    >
      {children}
    </div>
  );
}

export function StatCard({ icon, label, value, color, sub, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background:'#fff', borderRadius:12, padding:'14px 16px',
        boxShadow: hov ? '0 6px 20px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.07)',
        borderLeft:`4px solid ${color}`,
        display:'flex', alignItems:'center', gap:12, minWidth:0,
        transition:'all 0.15s', cursor: onClick ? 'pointer' : 'default',
        transform: hov ? 'translateY(-1px)' : 'none'
      }}
    >
      <div style={{ width:42, height:42, borderRadius:10, background:color+'18', display:'flex', alignItems:'center', justifyContent:'center', color, fontSize:18, flexShrink:0 }}>
        {icon}
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:22, fontWeight:800, color:'#1a1a2e', lineHeight:1.1 }}>{value}</div>
        <div style={{ fontSize:11, color:'#777', marginTop:2, fontWeight:500 }}>{label}</div>
        {sub && <div style={{ fontSize:11, color, marginTop:2, fontWeight:700 }}>{sub}</div>}
      </div>
    </div>
  );
}

export function Badge({ label, color, bg }) {
  return (
    <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700, color: color || '#fff', background: bg || '#e0e0e0' }}>
      {label}
    </span>
  );
}

export function ProgressBar({ value, color, height = 6 }) {
  return (
    <div style={{ height, background:'#f0f0f0', borderRadius:99, overflow:'hidden' }}>
      <div style={{ width:`${Math.min(value,100)}%`, height:'100%', background:color, borderRadius:99, transition:'width 0.6s ease' }} />
    </div>
  );
}

export function SectionHeader({ icon, title, color = MAROON, right }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, gap:8 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ color, fontSize:16 }}>{icon}</span>
        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#1a1a2e' }}>{title}</h3>
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position:'relative', flex:1 }}>
      <FaSearch style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#aaa', fontSize:13 }} />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%', boxSizing:'border-box', padding:'9px 10px 9px 32px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', background:'#fafafa' }}
      />
      {value && <FaTimes onClick={() => onChange('')} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'#aaa', cursor:'pointer' }} />}
    </div>
  );
}

// ─── Verification dots ────────────────────────────────────────────────────────
export function VerifDots({ v = {} }) {
  const items = [
    { key:'attendanceVerified', label:'Att' },
    { key:'diaryVerified', label:'Diary' },
    { key:'operationsVerified', label:'Ops' },
    { key:'publicHealthVerified', label:'PH' },
  ];
  return (
    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
      {items.map(it => (
        <span key={it.key} style={{ padding:'2px 7px', borderRadius:20, fontSize:10, fontWeight:700, background: v[it.key] ? '#27AE6018' : '#f0f0f0', color: v[it.key] ? '#27AE60' : '#aaa', border:`1px solid ${v[it.key] ? '#27AE6040' : '#e0e0e0'}` }}>
          {v[it.key] ? '✓ ' : ''}{it.label}
        </span>
      ))}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 40 }) {
  return (
    <div style={{ width:size, height:size, border:`3px solid ${MAROON}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
  );
}

// ─── Generic Modal ─────────────────────────────────────────────────────────────
export function Modal({ children, onClose, title, wide }) {
  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'16px', overflowY:'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth: wide ? 780 : 600, marginTop:8, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #f0f0f0', position:'sticky', top:0, background:'#fff', borderRadius:'16px 16px 0 0', zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a1a2e' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'#f0f0f0', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', color:'#666' }}>
            <FaTimes />
          </button>
        </div>
        <div style={{ padding:'16px 20px 20px' }}>{children}</div>
      </div>
    </div>
  );
}
